import React from 'react'
import { connect } from 'react-redux'
import { useStaticQuery, graphql, Link } from 'gatsby'

import transformLink from '~/utils/transformLink'

import trads from '~/intl/trads.json'

import { TwitterX } from '~/icons/Twitter'
import { Linkedin } from '~/icons/Linkedin'
import { Youtube } from '~/icons/Youtube'
import { Facebook } from '~/icons/Facebook'
import { User } from '~/icons/User'
import CookiesSettings from '~/components/core/cookiesSettings'
import { LogoFooterFr } from '~/icons/LogoFooterFr'
import { LogoFooterEn } from '~/icons/LogoFooterEn'

const Footer = ({ lang, hasBorder }) => {
  let globalSettings = null

  const datas = useStaticQuery(graphql`
    query {
      globalSettings: allContentfulGlobalSettings {
        nodes {
          title
          facebook
          footerPhoneNumber
          footerPhoneNumber2
          footerPhoneNumberLabel
          footerPhoneNumberLabel2
          headerPhoneNumber
          headerPhoneNumberLabel
          node_locale
          linkedin
          twitter
          youtube
          footerQuickAccessLinks {
            label
            pageLink {
              ... on Node {
                ... on ContentfulEvent {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulProject {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLabo {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepage {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulGovernance {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulProfile {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAction {
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulMap {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNewsroomList {
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
              }
            }
            customLink
            assetLink {
              file {
                url
              }
            }
          }
        }
      }
    }
  `)

  datas?.globalSettings?.nodes.forEach((item) => {
    if (item.node_locale === lang && item.title !== 'dummy content') {
      globalSettings = item
    }
  })

  return (
    <footer className={`footer ${hasBorder ? 'footer--border' : ''}`}>
      <div className="fluid-grid">
        <div className="row pb-5 md:pb-14">
          <div className="col-12 md-col-12 lg-col-4 lg-col-offset-2">
            <Link to={lang === 'fr' ? '/' : '/en'}>
              {lang === 'fr' ? (
                <LogoFooterFr className="w-[208px]" />
              ) : (
                <LogoFooterEn className="w-[208px]" />
              )}
            </Link>

            <ul className="flex flex-wrap justify-center md:justify-start mt-10 mb-7 lg:mb-0 lg:mt-16">
              {globalSettings?.twitter && (
                <li className="mr-2">
                  <a
                    className="footer__social"
                    href={globalSettings?.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Compte Twitter</span>
                    <TwitterX className="icon-purple text-purple w-8" />
                  </a>
                </li>
              )}

              {globalSettings?.facebook && (
                <li className="mr-2">
                  <a
                    className="footer__social"
                    href={globalSettings?.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Compte Facebook</span>
                    <Facebook className="icon-purple" />
                  </a>
                </li>
              )}

              {globalSettings?.linkedin && (
                <li className="mr-2">
                  <a
                    className="footer__social"
                    href={globalSettings?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Compte Linkedin</span>
                    <Linkedin className="icon-purple" />
                  </a>
                </li>
              )}

              {globalSettings?.youtube && (
                <li>
                  <a
                    className="footer__social"
                    href={globalSettings?.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Compte Youtube</span>
                    <Youtube className="icon-purple" />
                  </a>
                </li>
              )}
            </ul>
          </div>
          <div className="col-12 md-col-6 lg-col-3">
            <div className="lg:-ml-6">
              <p className="text-label text-blue mb-7 lg:mb-4 mt-4">
                {trads[lang]?.footer?.team}
              </p>

              <div className="inline-block">
                <div className="flex items-center justify-between mb-6 lg:mb-4">
                  <p className="text-13 font-semi-bold">
                    {globalSettings?.footerPhoneNumberLabel}
                  </p>
                  <a
                    href={`tel:${globalSettings?.footerPhoneNumber}`}
                    className="footer__phone"
                  >
                    {globalSettings?.footerPhoneNumber}
                  </a>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-13 font-semi-bold">
                    {globalSettings?.footerPhoneNumberLabel2}
                  </p>
                  <a
                    href={`tel:${globalSettings?.footerPhoneNumber2}`}
                    className="footer__phone"
                  >
                    {globalSettings?.footerPhoneNumber2}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 md-col-6 lg-col-2">
            <div className="lg:pl-8">
              <p className="text-label text-blue mb-5 mt-9 md:mt-4">
                {trads[lang]?.footer?.quickAccess}
              </p>
              <ul className="text-13 font-semi-bold footer__quick">
                {globalSettings?.footerQuickAccessLinks.map((link, i) => {
                  const url = transformLink(link, true, false).url
                  return (
                    <li className="mb-5" key={i}>
                      <Link to={url}>{link.label}</Link>
                    </li>
                  )
                })}
                <li>
                  <a
                    href="https://portail.terega.fr/star-app/accueil"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <User className="icon-light-green flex-shrink-0 -ml-2" />
                    {trads[lang]?.footer?.customerPortal}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="text-12 bg-purple text-white py-5">
        <div className="fluid-grid">
          <ul className="md:flex flex-wrap justify-center footer__legals">
            <li className="footer__legals-item">
              <Link
                  to={
                    lang === 'fr'
                        ? '/protection-des-donnees-personnelles'
                        : '/en/protection-of-personal-data'
                  }
              >
                {trads[lang]?.footer?.personalData}
              </Link>
            </li>
            <li className="footer__legals-item">
              <CookiesSettings text={trads[lang]?.footer?.cookies}/>
            </li>
            <li className="footer__legals-item">
              <Link
                  to={
                    lang === 'fr' ? '/plan-du-site-terega' : '/en/sitemap-terega'
                  }
              >
                {trads[lang]?.footer?.sitemap}
              </Link>
            </li>
            <li className="footer__legals-item">
              <Link
                  to={lang === 'fr' ? '/mentions-legales' : '/en/legal-notices'}
              >
                {trads[lang]?.footer?.legals}
              </Link>
            </li>
            <li className="footer__legals-item">
              <Link
                  to={lang === 'fr' ? '/declaration-daccessibilite' : '/en/accessibility-statement'}
              >
                {trads[lang]?.footer?.accessibility}
              </Link>
            </li>
            <li>©Terega {new Date().getFullYear()}</li>
          </ul>
        </div>
      </div>
    </footer>
  )
}

const mapStateToProps = ({lang}) => {
  return {lang}
}

export default connect(mapStateToProps, null)(Footer)
